import { useCallback, useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MergeIcon from "@mui/icons-material/MergeType";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import type {
  ExtendedTeam,
  MergeTeamPreviewResponse,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { Banner, BannerList } from "~/components/design-system/index.js";

export function TeamMergePage() {
  const [primaryTeamQuery, setPrimaryTeamQuery] = useState("");
  const [secondaryTeamQuery, setSecondaryTeamQuery] = useState("");
  const [primaryTeam, setPrimaryTeam] = useState<ExtendedTeam | null>(null);
  const [secondaryTeam, setSecondaryTeam] = useState<ExtendedTeam | null>(null);
  const [preview, setPreview] = useState<MergeTeamPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [mergeResult, setMergeResult] = useState<string | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [{ data: primaryResults }] = apiHooks.teams.search(
    primaryTeamQuery,
    10,
    0,
  );
  const [{ data: secondaryResults }] = apiHooks.teams.search(
    secondaryTeamQuery,
    10,
    0,
  );

  const handlePreview = useCallback(async () => {
    if (!primaryTeam || !secondaryTeam) return;
    setPreviewError(null);
    setPreview(null);
    setMergeResult(null);
    setMergeError(null);

    try {
      const response = await api.teams.mergePreview(
        primaryTeam.id,
        secondaryTeam.id,
      );
      setPreview(response.data);
    } catch (err: any) {
      setPreviewError(
        err?.response?.data?.reason ?? "Failed to generate preview",
      );
    }
  }, [primaryTeam, secondaryTeam]);

  const handleMerge = useCallback(async () => {
    if (!primaryTeam || !secondaryTeam) return;
    setConfirmOpen(false);
    setLoading(true);
    setMergeError(null);

    try {
      const response = await api.teams.merge(primaryTeam.id, {
        secondaryTeamId: secondaryTeam.id,
      });
      const r = response.data;
      setMergeResult(
        `Merge complete: ${r.workItemsTransferred} work items transferred, ` +
          `${r.workItemsMerged} conflicts resolved, ` +
          `${r.membershipsAdded} memberships added.`,
      );
      setPreview(null);
      setSecondaryTeam(null);
    } catch (err: any) {
      setMergeError(err?.response?.data?.reason ?? "Merge failed");
    } finally {
      setLoading(false);
    }
  }, [primaryTeam, secondaryTeam]);

  const canPreview =
    primaryTeam && secondaryTeam && primaryTeam.id !== secondaryTeam.id;

  return (
    <Box>
      <Box sx={{ marginBottom: "1em" }}>
        <Banner
          title="Merge two teams by transferring all work items and memberships from the secondary team into the primary team. The secondary team will be soft-deleted and an email redirect will be created automatically."
          bannerType={BannerList.INFO}
          dense={true}
        />
      </Box>

      <Paper sx={{ padding: "1.5em", marginBottom: "2em" }}>
        <Typography variant="h6" sx={{ marginBottom: "1em" }}>
          Select Teams
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: "1em",
            alignItems: "center",
            marginBottom: "1em",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ marginBottom: "0.5em", color: "error.main" }}
            >
              Team to close (will be deleted)
            </Typography>
            <Autocomplete
              options={secondaryResults?.results ?? []}
              getOptionLabel={(option) =>
                `${option.name} (${option.type}) — ${option.primaryEmail}`
              }
              value={secondaryTeam}
              onChange={(_e, value) => {
                setSecondaryTeam(value);
                setPreview(null);
              }}
              onInputChange={(_e, value) => setSecondaryTeamQuery(value)}
              renderInput={(params) => (
                <TextField {...params} label="Secondary team" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>

          <ArrowForwardIcon
            sx={{ fontSize: 40, color: "text.secondary", mt: 3 }}
          />

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ marginBottom: "0.5em", color: "success.main" }}
            >
              Destination team (will remain active)
            </Typography>
            <Autocomplete
              options={primaryResults?.results ?? []}
              getOptionLabel={(option) =>
                `${option.name} (${option.type}) — ${option.primaryEmail}`
              }
              value={primaryTeam}
              onChange={(_e, value) => {
                setPrimaryTeam(value);
                setPreview(null);
              }}
              onInputChange={(_e, value) => setPrimaryTeamQuery(value)}
              renderInput={(params) => (
                <TextField {...params} label="Primary team" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>
        </Box>

        {secondaryTeam && primaryTeam && (
          <Box
            sx={{
              backgroundColor: "grey.100",
              borderRadius: 1,
              padding: "1em",
              marginBottom: "1em",
              textAlign: "center",
            }}
          >
            <Typography>
              All work items and members from{" "}
              <strong>{secondaryTeam.name}</strong> will be moved to{" "}
              <strong>{primaryTeam.name}</strong>.{" "}
              <strong>{secondaryTeam.name}</strong> will then be deleted.
            </Typography>
          </Box>
        )}

        <Button
          variant="outlined"
          disabled={!canPreview}
          onClick={handlePreview}
        >
          Preview Merge
        </Button>
      </Paper>

      {previewError && (
        <Alert severity="error" sx={{ marginBottom: "1em" }}>
          {previewError}
        </Alert>
      )}

      {preview && (
        <Paper sx={{ padding: "1.5em", marginBottom: "2em" }}>
          <Typography variant="h6" sx={{ marginBottom: "1em" }}>
            Merge Preview
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.5em",
              marginBottom: "1.5em",
            }}
          >
            <Typography>Work items to transfer:</Typography>
            <Typography fontWeight="bold">
              {preview.workItemsToTransfer}
            </Typography>

            <Typography>Work item conflicts to resolve:</Typography>
            <Typography fontWeight="bold">
              {preview.workItemConflicts}
            </Typography>

            <Typography>Memberships to add:</Typography>
            <Typography fontWeight="bold">
              {preview.membershipsToAdd}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="warning"
            disabled={loading}
            onClick={() => setConfirmOpen(true)}
            startIcon={<MergeIcon />}
          >
            {loading ? "Merging…" : "Merge Teams"}
          </Button>
        </Paper>
      )}

      {mergeResult && (
        <Alert severity="success" sx={{ marginBottom: "1em" }}>
          {mergeResult}
        </Alert>
      )}

      {mergeError && (
        <Alert severity="error" sx={{ marginBottom: "1em" }}>
          {mergeError}
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Team Merge</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to merge <strong>{secondaryTeam?.name}</strong> into{" "}
            <strong>{primaryTeam?.name}</strong>. This will:
          </DialogContentText>
          <Box component="ul" sx={{ marginTop: "0.5em" }}>
            <li>
              Transfer {preview?.workItemsToTransfer} work items and resolve{" "}
              {preview?.workItemConflicts} conflicts
            </li>
            <li>
              Add {preview?.membershipsToAdd} memberships to the primary team
            </li>
            <li>
              Create an email redirect from the secondary team&apos;s email
            </li>
            <li>Soft-delete the secondary team</li>
          </Box>
          <DialogContentText sx={{ marginTop: "1em", fontWeight: "bold" }}>
            This action cannot be easily undone. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleMerge}
            autoFocus
          >
            Confirm Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
